import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
// import { supabase } from '../supabase'; // <-- Temporaneamente disabilitato
import { 
  MOCK_USERS, 
  MOCK_CHATS, 
  MOCK_NOTIFICATIONS, 
  GAME_DATA, 
  GLOBAL_OPTIONS 
} from '../data/mockData';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // ─── STATE ────────────────────────────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userStatus, setUserStatus] = useState('online');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('nakama_theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Dati Mockati in memoria
  const [users, setUsers] = useState(MOCK_USERS);
  const [chats, setChats] = useState(MOCK_CHATS);
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const [friends, setFriends] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [globalGames, setGlobalGames] = useState(GAME_DATA);
  const [globalOptions, setGlobalOptions] = useState(GLOBAL_OPTIONS);

  // ─── DARK MODE ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('nakama_theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('nakama_theme', 'light');
    }
  }, [isDarkMode]);

  // ─── MOCK INIT ────────────────────────────────────────────────────────────
  useEffect(() => {
    // Simuliamo un piccolo ritardo di caricamento tipico di un'app reale
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Quando l'utente logga, simuliamo il caricamento dei suoi dati sociali
  useEffect(() => {
    if (currentUser) {
      if (currentUser.id === 'u0') {
        setFriends(['u3', 'u4']); // Admin è amico con u3 e u4
        setReceivedRequests(['u1']); // Ha una richiesta da u1
      } else {
        setFriends([]);
        setReceivedRequests([]);
      }
      setPendingRequests([]);
      setBlockedUsers([]);
    }
  }, [currentUser]);

  // ─── AUTH (MOCKED) ────────────────────────────────────────────────────────
  const checkNicknameAvailability = async (nickname) => {
    return !users.some(u => u.nickname.toLowerCase() === nickname.toLowerCase());
  };

  const login = async (email, password) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const user = users.find(u => u.email === email && u.password === password);
        if (user) {
          setCurrentUser(user);
          resolve({ success: true });
        } else {
          resolve({ 
            success: false, 
            message: 'Email o password non corretti. Prova admin@nakama.gg / admin123' 
          });
        }
      }, 600);
    });
  };

  const register = async (userData) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const { email, password, nickname, firstName, lastName, dob, gender, country } = userData;
        
        if (users.some(u => u.email === email)) {
          return resolve({ success: false, message: 'Email già in uso' });
        }
        if (users.some(u => u.nickname.toLowerCase() === nickname.toLowerCase())) {
          return resolve({ success: false, message: 'Nickname già in uso' });
        }

        const newUser = {
          id: `u${Date.now()}`,
          email,
          password,
          nickname,
          name: `${firstName} ${lastName}`.trim(),
          avatar: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?auto=format&fit=crop&q=80&w=150&h=150',
          role: 'player',
          status: 'active',
          isOnline: true,
          languages: ['English'],
          nationality: country,
          timezone: 'CET (UTC+1)',
          bio: 'Nuovo giocatore su Nakama!',
          birthDate: dob,
          gender,
          privacySettings: {
            showRealName: true, showNationality: true, showTimezone: true, showBio: true, showLanguages: true,
          },
          games: []
        };

        setUsers(prev => [...prev, newUser]);
        setCurrentUser(newUser); // Auto-login
        resolve({ success: true, confirmationRequired: false });
      }, 800);
    });
  };

  const logout = async () => {
    setCurrentUser(null);
    setChats([]);
    setNotifications([]);
  };

  const updateProfile = async (newData) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const updated = { ...currentUser, ...newData };
        setCurrentUser(updated);
        setUsers(prev => prev.map(u => u.id === currentUser.id ? updated : u));
        resolve({ success: true });
      }, 400);
    });
  };

  // ─── FRIENDS (MOCKED) ─────────────────────────────────────────────────────
  const sendRequest = async (targetId) => {
    setPendingRequests(prev => [...prev, targetId]);
  };

  const acceptRequest = async (senderId) => {
    setReceivedRequests(prev => prev.filter(id => id !== senderId));
    setFriends(prev => [...prev, senderId]);
  };

  const rejectRequest = async (senderId) => {
    setReceivedRequests(prev => prev.filter(id => id !== senderId));
  };

  const unfriend = async (friendId) => {
    setFriends(prev => prev.filter(id => id !== friendId));
  };

  const blockPlayer = async (targetId) => {
    setBlockedUsers(prev => [...prev, targetId]);
    setFriends(prev => prev.filter(id => id !== targetId));
  };

  const unblockPlayer = async (targetId) => {
    setBlockedUsers(prev => prev.filter(id => id !== targetId));
  };

  // ─── CHAT (MOCKED) ────────────────────────────────────────────────────────
  const getOrCreateChat = (userId) => {
    const existing = chats.find(c => c.user.id === userId);
    if (existing) return existing.id;

    const targetUser = users.find(u => u.id === userId);
    const newChatId = `c${Date.now()}`;
    const newChat = {
      id: newChatId,
      user: { id: targetUser.id, nickname: targetUser.nickname, avatar: targetUser.avatar, isOnline: targetUser.isOnline },
      lastMessage: '',
      time: '',
      unread: 0,
      messages: []
    };
    setChats(prev => [newChat, ...prev]);
    return newChatId;
  };

  const sendMessage = async (chatId, text) => {
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setChats(prev => prev.map(chat => {
      if (chat.id !== chatId) return chat;
      return {
        ...chat,
        lastMessage: text,
        time: now,
        messages: [...chat.messages, { sender: currentUser.id, text, time: now }]
      };
    }));
  };

  const markChatAsRead = async (chatId) => {
    setChats(prev => prev.map(c => c.id === chatId ? { ...c, unread: 0 } : c));
  };

  // ─── NOTIFICATIONS (MOCKED) ───────────────────────────────────────────────
  const markNotificationsAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // ─── USER GAMES (MOCKED) ──────────────────────────────────────────────────
  const addGame = async (game) => {
    setCurrentUser(prev => {
      const updated = { ...prev, games: [...(prev.games || []), game] };
      setUsers(u => u.map(x => x.id === prev.id ? updated : x));
      return updated;
    });
  };

  const removeGame = async (gameName) => {
    setCurrentUser(prev => {
      const updated = { ...prev, games: prev.games.filter(g => g.name !== gameName) };
      setUsers(u => u.map(x => x.id === prev.id ? updated : x));
      return updated;
    });
  };

  const updateUserGame = async (oldName, newGame) => {
    setCurrentUser(prev => {
      const updated = { ...prev, games: prev.games.map(g => g.name === oldName ? newGame : g) };
      setUsers(u => u.map(x => x.id === prev.id ? updated : x));
      return updated;
    });
  };

  // ─── ADMIN (MOCKED) ───────────────────────────────────────────────────────
  const updateUserStatus = async (userId, status) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, status } : u));
  };

  const updateUserRole = async (userId, role) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
  };

  const resetPassword = async (userId) => {
    console.log(`Password reset for user ${userId}`);
  };

  const addGlobalGame = async (name, roles, ranks) => {
    setGlobalGames(prev => ({ ...prev, [name]: { roles, ranks } }));
  };

  const updateGlobalGame = async (oldName, newName, roles, ranks) => {
    setGlobalGames(prev => {
      const updated = { ...prev };
      delete updated[oldName];
      updated[newName] = { roles, ranks };
      return updated;
    });
  };

  const deleteGlobalGame = async (name) => {
    setGlobalGames(prev => {
      const updated = { ...prev };
      delete updated[name];
      return updated;
    });
  };

  const addGlobalOption = (key, value) => {
    setGlobalOptions(prev => ({ ...prev, [key]: [...prev[key], value] }));
  };
  const removeGlobalOption = (key, value) => {
    setGlobalOptions(prev => ({ ...prev, [key]: prev[key].filter(v => v !== value) }));
  };
  const updateGlobalOption = (key, oldVal, newVal) => {
    setGlobalOptions(prev => ({ ...prev, [key]: prev[key].map(v => v === oldVal ? newVal : v) }));
  };

  // ─── MISC ─────────────────────────────────────────────────────────────────
  const toggleDarkMode = () => setIsDarkMode(d => !d);

  const toggleStatus = async () => {
    const newStatus = userStatus === 'online' ? 'invisible' : 'online';
    setUserStatus(newStatus);
    if (currentUser) {
      setCurrentUser(prev => ({ ...prev, isOnline: newStatus === 'online' }));
    }
  };

  const value = {
    currentUser, isInitialized, isDarkMode, userStatus, users, chats, notifications,
    friends, receivedRequests, pendingRequests, blockedUsers, globalGames, globalOptions,
    unreadNotifications: notifications.filter(n => !n.read).length,
    isFriend: (id) => friends.includes(id),
    isBlocked: (id) => blockedUsers.includes(id),
    isRequestPending: (id) => pendingRequests.includes(id),
    hasReceivedRequest: (id) => receivedRequests.includes(id),
    login, register, logout, updateProfile, checkNicknameAvailability, toggleDarkMode, toggleStatus,
    sendRequest, acceptRequest, rejectRequest, unfriend, blockPlayer, unblockPlayer,
    getOrCreateChat, sendMessage, markChatAsRead, markNotificationsAsRead,
    addGame, removeGame, updateUserGame,
    updateUserStatus, updateUserRole, resetPassword,
    addGlobalGame, updateGlobalGame, deleteGlobalGame,
    addGlobalOption, removeGlobalOption, updateGlobalOption,
    refreshUsers: () => {},
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);